// Centralized content for the Joseph Helfrick personal injury site.
// Used by pages for SEO copy, structured data (JSON-LD) and rendering.

export const ATTORNEY = {
    name: "Joseph G. Helfrick",
    shortName: "Joe Helfrick",
    role: "Personal Injury Attorney",
    headline: "Sacramento Personal Injury Attorney",
    phone: process.env.REACT_APP_PHONE || "(916) 492-2000",
    email: process.env.REACT_APP_EMAIL || "JHelfrick@ltglaw.net",
    firm: "Lauria Tokunaga Gates & Linn, LLP",
    address: {
        street: "1755 Creekside Oaks Drive, Suite 240",
        city: "Sacramento",
        region: "CA",
        postal: "95833",
        country: "US",
    },
    bio: `Born and raised in Sacramento, Joe Helfrick built his career on what people in Northern California already know — fight hard, listen first, and never let an insurance company decide what your future is worth. After a decade representing major civil clients in courtrooms across the region, Joe focuses his practice on standing beside the injured: drivers run off the road, patients hurt by careless medical care, workers told to walk it off, and families left to pick up the pieces.`,
    bioShort: `Sacramento-born and trained in Northern California's toughest courtrooms, Joe Helfrick represents people seriously hurt by the negligence of drivers, doctors, employers, and property owners — as well as those accused of the same.`,
    education: [
        {
            school: "Lincoln Law School",
            degree: "Juris Doctor (J.D.)",
            year: "2015",
            note: "Faculty Achievement Award — Highest Grade in Legal Analysis",
        },
        {
            school: "Holy Cross College of Notre Dame",
            degree: "Bachelor of Arts, History",
            year: "2010",
        },
        {
            school: "Jesuit High School (Sacramento)",
            degree: "High School Diploma",
            year: "",
        },
    ],
    admissions: [
        "California State Bar — 2017",
        "United States District Court, Eastern District of California — 2020",
    ],
    memberships: [
        "Association of Defense Counsel of Northern California and Nevada",
        "Sacramento County Bar Association",
    ],
};

export const PRACTICE_AREAS = [
    {
        slug: "car-and-truck-accidents",
        title: "Car & Truck Accidents",
        size: "large",
        lede: "Highway 80, I-5, Capital City Freeway, the back roads of Placer County — Northern California drives hard, and the crashes are getting worse.",
        body: `From rear-end collisions on the way to work to catastrophic 18-wheeler wrecks on I-80, motor vehicle crashes are the single most common cause of serious injury we see. The damage is rarely just the bumper. Whiplash, herniated discs, traumatic brain injuries, surgeries, months of missed work, and a permanent change in what your body can do.

We handle the full motor-vehicle universe: car-on-car collisions, commercial trucks, rideshare (Uber/Lyft), motorcycle crashes, bicycle and pedestrian impacts, hit-and-run, drunk and distracted drivers, and uninsured/underinsured motorist claims. Trucking cases get treated like the federal-regulation puzzles they are — we pull driver logs, ECM data, drug-test records, and maintenance histories before they "disappear."`,
        bullets: [
            "Multi-vehicle freeway collisions",
            "Commercial truck & 18-wheeler crashes",
            "Rideshare (Uber / Lyft) passengers and drivers",
            "Motorcycle, bicycle, and pedestrian injuries",
            "Drunk, distracted, and hit-and-run drivers",
            "Uninsured / underinsured motorist (UM/UIM) claims",
        ],
    },
    {
        slug: "slip-and-fall",
        title: "Slip, Trip & Fall",
        size: "small",
        lede: "Property owners owe you a safe walk — not a hospital bill.",
        body: `A wet grocery aisle with no sign. A broken stair at an apartment complex. An unlit parking lot in a Roseville strip mall. California premises liability law requires property owners to inspect, warn, and fix — and when they cut corners, the people who pay are the customers, tenants, and visitors they invited in. We move quickly to lock in surveillance footage and incident reports before they're overwritten or "lost."`,
        bullets: [
            "Grocery, retail, and restaurant falls",
            "Apartment & HOA common-area injuries",
            "Hotel, casino, and parking-lot incidents",
            "Stairway, handrail, and elevator failures",
        ],
    },
    {
        slug: "medical-and-dental-malpractice",
        title: "Medical & Dental Malpractice",
        size: "large",
        lede: "When the people you trusted with your body cause the harm.",
        body: `Med-mal and dental cases are different — different rules, different deadlines (MICRA notice, statutes of limitation that can run in as little as one year), different experts, different defense playbooks. Joe spent years on the defense side of these exact cases and knows how hospitals, insurers, and dental groups build their files. We use that against them.

Typical cases include surgical errors, anesthesia complications, missed or delayed diagnosis (especially cancer and stroke), medication and dosing errors, birth injuries, nursing-home neglect, infections from improper sterilization, and dental nerve and implant injuries.`,
        bullets: [
            "Surgical and anesthesia errors",
            "Missed or delayed diagnosis (cancer, stroke, cardiac)",
            "Birth injuries to mother or child",
            "Dental nerve damage, failed implants, sedation errors",
            "Nursing home and elder-care neglect",
            "Hospital-acquired infections (HAIs)",
        ],
    },
    {
        slug: "dog-bites-and-animal-attacks",
        title: "Dog Bites & Animal Attacks",
        size: "small",
        lede: "California is a strict-liability state. The owner is on the hook — even for a 'first bite.'",
        body: `Under California Civil Code § 3342, a dog owner is liable for bite injuries the moment the bite happens — no excuses about the dog "never doing that before." We pursue homeowner's and renter's policies for medical bills, scarring, plastic-surgery costs, lost wages, and the long psychological toll, especially on children.`,
        bullets: [
            "Dog bites and maulings (strict liability)",
            "Scarring and reconstructive surgery claims",
            "Bites to children and elderly victims",
            "Horse, livestock, and other animal injuries",
        ],
    },
    {
        slug: "workplace-accidents",
        title: "Workplace Accidents",
        size: "large",
        lede: "Workers' comp is not the whole story. There is almost always a third party.",
        body: `Workers' compensation in California is no-fault but limited. What most injured workers don't realize: if anyone other than your direct employer contributed to the injury — a defective tool, a subcontractor on the jobsite, a driver who hit your work truck, a property owner — you have a separate "third-party" personal injury claim that can recover the full value of pain, suffering, and future losses comp will never pay.

We work with workers' comp counsel to make sure your benefits keep flowing while we pursue the full-value civil claim.`,
        bullets: [
            "Construction and trade injuries",
            "Defective tools and machinery (product liability)",
            "Warehouse, forklift, and loading-dock incidents",
            "Driving-for-work crashes",
            "Falls from heights, scaffolding, and ladders",
        ],
    },
    {
        slug: "other-claims",
        title: "Other Claims",
        size: "small",
        lede: "Ski accidents. Government claims. Property disputes. The cases other firms turn away.",
        body: `Some cases need a lawyer who knows Northern California specifically — the ski resorts in Tahoe and the unique waiver law that comes with them, the strict six-month deadlines for tort claims against cities and counties (Govt. Code § 911.2), the property-line and easement disputes that come with mountain and rural land. Joe takes the cases that don't fit a billboard ad.`,
        bullets: [
            "Ski and snowboard injuries (Tahoe / Sierra resorts)",
            "Government tort claims (cities, counties, agencies)",
            "Property line, easement, and nuisance disputes",
            "Boating and recreational injuries",
        ],
    },
];

export const DAMAGES = [
    {
        slug: "economic",
        type: "Economic",
        tagline: "Measurable Financial Losses",
        oneliner:
            "The receipts. Every dollar the injury cost — already spent or coming.",
        body: `Economic damages are the part of your case you can put a number on. Past medical bills. Future medical bills (often the biggest single item — surgeries that haven't happened yet, therapy that will run for years, prescriptions, assistive devices). Lost wages from missed work. Loss of earning capacity if you can no longer do the job you trained for. Property damage to your vehicle. Out-of-pocket costs for transportation to appointments, home modifications, even hired help with chores you used to do yourself.`,
        examples: [
            "Past and future medical and rehabilitative care",
            "Lost wages and lost earning capacity",
            "Property damage and diminished value",
            "Out-of-pocket expenses (mileage, parking, medical equipment)",
            "Home and vehicle modifications for disability",
            "Replacement household services",
        ],
    },
    {
        slug: "non-economic",
        type: "Non-Economic",
        tagline: "The Human Cost of an Injury",
        oneliner:
            "Pain, fear, loss of self. The part the insurance company would rather pretend doesn't count.",
        body: `Non-economic damages compensate the harm that doesn't show up on a billing statement: physical pain, mental anguish, anxiety, depression, PTSD, loss of enjoyment of life, scarring and disfigurement, loss of consortium (impact on your marriage and intimacy), and the inability to do the things that used to make you who you are — coaching your kid's team, gardening, hiking the American River Parkway. California does not cap non-economic damages in ordinary injury cases. (Medical malpractice has its own MICRA cap structure, which we will explain to you in plain English.)`,
        examples: [
            "Physical pain and suffering",
            "Mental and emotional distress (anxiety, depression, PTSD)",
            "Loss of enjoyment of life",
            "Disfigurement and visible scarring",
            "Loss of consortium (spouse / family)",
            "Inconvenience and humiliation",
        ],
    },
    {
        slug: "punitive",
        type: "Punitive",
        tagline: "Reserved for the Worst Conduct",
        oneliner:
            "Not for ordinary mistakes — for malice, oppression, or fraud.",
        body: `Punitive damages are not awarded in most cases. California Civil Code § 3294 requires "clear and convincing evidence" that the defendant acted with malice, oppression, or fraud. We pursue them in the cases that demand them: a drunk driver with a history of DUIs, a corporation that knowingly sold a defective product, a nursing-home chain that hid abuse, a trucking company that falsified driver logs. Punitive damages are meant to punish and to deter — and they can dramatically change the value of a case.`,
        examples: [
            "Repeat-offense drunk drivers",
            "Knowing concealment of a product defect",
            "Elder abuse and willful neglect",
            "Falsified safety records (trucking, construction)",
            "Intentional fraud causing physical harm",
        ],
    },
];

export const SACRAMENTO_COUNTY_CITIES = [
    "Sacramento",
    "Elk Grove",
    "Folsom",
    "Citrus Heights",
    "Rancho Cordova",
    "Galt",
    "Isleton",
    "Carmichael",
    "Arden-Arcade",
    "North Highlands",
    "Orangevale",
    "Fair Oaks",
    "Antelope",
    "Florin",
    "Rosemont",
    "Vineyard",
    "Rio Linda",
    "Elverta",
    "Wilton",
    "Walnut Grove",
];

export const PLACER_COUNTY_CITIES = [
    "Roseville",
    "Rocklin",
    "Lincoln",
    "Auburn",
    "Loomis",
    "Colfax",
    "Granite Bay",
    "Penryn",
    "Newcastle",
    "Foresthill",
    "Meadow Vista",
    "Tahoe City",
    "Kings Beach",
    "Truckee (border)",
    "Dutch Flat",
    "Alta",
];

export const FAQS = [
    {
        q: "Who is Joseph G. Helfrick?",
        a: "Joseph G. Helfrick (Joe Helfrick) is a Sacramento-born personal injury attorney who represents people seriously injured in Sacramento, CA, and surrounding areas. He earned his J.D. from Lincoln Law School (2015) and was admitted to the California State Bar in 2017.",
    },
    {
        q: "Where is Joe Helfrick's law office located?",
        a: "Joe Helfrick's office is at 1755 Creekside Oaks Drive, Suite 240, Sacramento, CA 95833. The office serves clients throughout Sacramento, CA, and surrounding areas.",
    },
    {
        q: "What is Joe Helfrick's phone number?",
        a: "You can reach Joe Helfrick's office at (916) 492-2000. Consultations are free and confidential.",
    },
    {
        q: "What types of personal injury cases does Joe Helfrick handle?",
        a: "Joe handles car and truck accidents, slip and fall (premises liability), medical and dental malpractice, dog bites and animal attacks, workplace injuries, ski accidents, government tort claims, and related property and recreational injury disputes.",
    },
    {
        q: "How much does it cost to hire Joe Helfrick?",
        a: "Nothing up front. Personal injury cases are taken on a contingency fee — you pay only if we recover money for you. The consultation is always free and confidential.",
    },
    {
        q: "How long do I have to file a personal injury claim in California?",
        a: "The general statute of limitations for personal injury in California is two years from the date of injury. Medical malpractice can be as short as one year (with notice requirements). Claims against a government entity typically require a written claim within six months under Government Code § 911.2.",
    },
    {
        q: "What if I was partly at fault for the accident?",
        a: "California follows pure comparative negligence. Even if you are partly responsible, you can still recover compensation — your recovery is reduced by your percentage of fault. Almost no case is hopeless on this ground alone.",
    },
    {
        q: "Do I have to go to court?",
        a: "Most personal injury cases settle without trial. But insurance companies offer their best numbers to lawyers they know are willing — and prepared — to take the case to a jury. Every file is prepared as if it is going to verdict.",
    },
    {
        q: "What cities does Joe Helfrick serve?",
        a: "Joe represents clients in Sacramento, Elk Grove, Folsom, Citrus Heights, Rancho Cordova, Galt, Carmichael, Fair Oaks, and other Sacramento County communities, plus Roseville, Rocklin, Lincoln, Auburn, Loomis, Granite Bay, Tahoe City, and other Placer County cities.",
    },
    {
        q: "What should I do right after an accident in Sacramento?",
        a: "Get medical care first. Then document everything — photos of the scene, vehicles, and injuries; names and numbers of witnesses; police or incident reports. Do not give a recorded statement to the other side's insurance company before you talk to a lawyer.",
    },
    {
        q: "What are economic damages in a California personal injury case?",
        a: "Economic damages are measurable financial losses: past and future medical bills, lost wages, loss of earning capacity, property damage, out-of-pocket expenses, home or vehicle modifications, and the cost of replacement household services.",
    },
    {
        q: "What are non-economic damages?",
        a: "Non-economic damages compensate the human cost of an injury: pain and suffering, mental anguish, anxiety, depression, PTSD, loss of enjoyment of life, scarring and disfigurement, and loss of consortium. California does not cap non-economic damages in most injury cases.",
    },
    {
        q: "When are punitive damages awarded in California?",
        a: "Punitive damages are reserved for the worst conduct. Under California Civil Code § 3294, the plaintiff must prove by clear and convincing evidence that the defendant acted with malice, oppression, or fraud — for example, a repeat-offense drunk driver or a company that knowingly concealed a product defect.",
    },
    {
        q: "Is California a strict-liability state for dog bites?",
        a: "Yes. Under California Civil Code § 3342, a dog owner is liable for bite injuries the moment they happen — even on the first bite — with very limited exceptions. Recovery typically comes through the owner's homeowner's or renter's insurance.",
    },
    {
        q: "What is the deadline to file a claim against a city or county in California?",
        a: "Claims against a California city, county, or state agency generally require a written tort claim presented within six months of the incident (Government Code § 911.2). Missing this short deadline almost always bars the claim, so do not wait.",
    },
];

